import {useSnackbar} from 'notistack'
import {useRef, useState} from 'react'
import {ListFacialRecognitionDTO} from 'src/common/models/facial-recognition/ListFacialRecognitionDTO'
import {ValidateUserPermissions} from 'src/common/utils/ValidatePermissions'
import api from 'src/services/api'
import {LicensePlateList} from 'src/services/api/endpoints/LprEndpoint'
import {Paginate} from 'src/services/api/endpoints/_types'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'

export type List = {
  id: number
  name: string
  clientId: number
  companyId: number
  client: string
  listColor?: string
  type: 'Faces' | 'Placas'
}

type ListProps = {
  filter?: string
  page: number
  companyIds?: number[]
}

function useLists(props: ListProps) {
  const {filter: nameFilter, page, companyIds} = props
  const [lists, setLists] = useState<List[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [canLoadMore, setCanLoadMore] = useState(true)
  const hasListFacePermission = ValidateUserPermissions('face:read')
  const hasListLprPermission = ValidateUserPermissions('lpr:read')
  const blockRequests = useRef(false)

  const {enqueueSnackbar} = useSnackbar()

  async function getLists(replace: boolean = true) {
    if (blockRequests.current) return
    blockRequests.current = true
    setIsLoading(true)
    try {
      let facialLists: Paginate<ListFacialRecognitionDTO> = {} as Paginate<ListFacialRecognitionDTO>
      let lprLists: Paginate<LicensePlateList> = {} as Paginate<LicensePlateList>
      const mergedList: List[] = replace ? [] : lists

      if (hasListFacePermission) facialLists = (await api.facialRecognition.getList({search: {listName: nameFilter}, filter: {companyIds}})).data.data
      if (hasListLprPermission) lprLists = (await api.lpr.getManyLists({page, pageSize: 20, search: {listName: nameFilter}, filter: {companyIds}})).data.data

      if (facialLists.totalElements !== 20 && lprLists.totalElements !== 20) setCanLoadMore(false)

      const clientsEntities = (await api.client.getMany({page, pageSize: 100, filter: {companyIds}})).data.data.entities
      if (hasListFacePermission && facialLists.entities.length)
        facialLists.entities.forEach((list) => {
          return mergedList.push({
            id: list.id,
            name: list.name,
            clientId: list.clientId,
            companyId: list.companyId,
            listColor: list.listColor ?? undefined,
            client: clientsEntities.find((client) => client.id === list.clientId)?.name || 'Lista compartilhada',
            type: 'Faces',
          })
        })

      if (hasListLprPermission && lprLists.entities.length)
        lprLists.entities.forEach((list) => {
          return mergedList.push({
            id: list.id,
            name: list.name,
            clientId: list.clientId,
            companyId: list.companyId,
            client: clientsEntities.find((client) => client.id === list.clientId)?.name || 'Lista compartilhada',
            listColor: list.listColor ?? undefined,
            type: 'Placas',
          })
        })

      setLists(mergedList.filter((value, index, array) => array.findIndex((t) => t.id === value.id && t.type === value.type) === index))
    } catch (err) {
      handleErrorWithSnackbar(enqueueSnackbar, err, 'Não foi possível carregar listas cadastradas')
    } finally {
      blockRequests.current = false
      setIsLoading(false)
    }
  }

  async function deleteManyLists(facesIds: number[], platesIds: number[]) {
    if (!hasListFacePermission || !hasListLprPermission) return
    try {
      setIsLoading(true)
      if (hasListFacePermission && facesIds.length) {
        await api.facialRecognition.deleteManyFacialList(facesIds)
      }

      if (hasListLprPermission && platesIds.length) {
        await api.lpr.deleteManyPlateLists(platesIds)
      }
    } catch (err) {
      enqueueSnackbar('Erro ao deletar listas', {variant: 'error'})
    } finally {
      setIsLoading(false)
    }
  }

  return {lists, isLoading, canLoadMore, getLists, deleteManyLists}
}

export default useLists
